import styles from "./shared.module.scss";
import agGridStyles from "./ag_grid_styles.module.scss";
import AgGridTable from "../../../components/table/AgGridTable";
import SortDropdown from "./sortdropdown";
import ColumnSelectionMenu from "./column_selection_menu";
import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import SkeletonLoader from "./skeleton_loader";
import { Button, Switch } from "@mui/material";
import select_col_light from '../../../assets/select_col_light.svg'

interface TableCardProps {
  title: any;
  columnDefs: any;
  rowData: any;
  showSearch: boolean;
  isRowSelectable: boolean;
  gridOptions?: any;
  gridRef?: any;
  loading: boolean;
  showFilter?: boolean;
  sortOptions?: any[];
  setSortBy?: (value: string) => void;
  sortBy?: string;
  onRowClick?: (data: any) => void;
  columDefOn?: boolean;
  nistColumns?: string[];
  cisColumns?: string[];
  pciDssColumns?: string[];
  sparkColumns?: string[];
  onColumnSelection?: (selectedNist: string, selectedCis: string, selectedPciDss: string, selectedSpark?: string) => void;
  currentSelectedNist?: string;
  currentSelectedCis?: string;
  currentSelectedPciDss?: string;
  currentSelectedSpark?: string;
  toggle?: boolean;
  toggleChecked?: boolean;
  onToggleChange?: (checked: boolean) => void;
  toggleLabelLeft?: string;
  toggleLabelRight?: string;
}

const TableCard: React.FC<TableCardProps> = ({
  title,
  columnDefs,
  rowData,
  showSearch,
  isRowSelectable,
  gridOptions,
  gridRef,
  loading,
  showFilter = false,
  sortOptions = [],
  setSortBy,
  sortBy,
  onRowClick,
  columDefOn = false,
  nistColumns = [],
  cisColumns = [],
  pciDssColumns = [],
  sparkColumns = [],
  onColumnSelection,
  currentSelectedNist,
  currentSelectedCis,
  currentSelectedPciDss,
  currentSelectedSpark,
  toggle = false,
  toggleChecked = false,
  onToggleChange,
  toggleLabelLeft = "On-Prem",
  toggleLabelRight = "Cloud",
}) => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      ...gridOptions,
      headerClass: theme.palette.mode === "dark" ? agGridStyles["ag-header-dark"] : agGridStyles["ag-header-light"],
      rowClass: theme.palette.mode === "dark" ? agGridStyles["ag-row-dark"] : agGridStyles["ag-row-light"],
    }),
    [gridOptions, theme.palette.mode]
  );

  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  return (
    <div data-theme={theme.palette.mode} style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div className={styles.container} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className={styles.header} style={{ position: 'relative', flexShrink: 0 }}>
          <h2 style={{ width: '100%', marginRight: '10px' }}>{title}</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {toggle && (
              <>
                <span>{toggleLabelLeft}</span>
                <Switch
                  checked={toggleChecked}
                  onChange={e => onToggleChange?.(e.target.checked)}
                  color="primary"
                />
                <span>{toggleLabelRight}</span>
              </>
            )}
            {columDefOn && (
              <Button
                onClick={handleMenuClick}
                size="small"
                disableRipple
                disableFocusRipple
                className="no-drag"
                sx={{
                  minWidth: 'auto',
                  padding: 0,
                  background: 'none',
                  boxShadow: 'none',
                  border: 'none',
                  '&:hover': {
                    background: 'none',
                    boxShadow: 'none',
                    cursor: 'pointer',
                  },
                  '&:active': {
                    background: 'none',
                    boxShadow: 'none',
                  },
                }}
              >
                <img src={select_col_light} alt="select_col_light" style={{ display: 'block' }} />
              </Button>
            )}
            {showFilter && sortBy && setSortBy && (
              <SortDropdown
                options={sortOptions}
                setSortBy={setSortBy}
                sortBy={sortBy}
              />
            )}
          </div>
        </div>
        <div className={styles.data_container} style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden' }}>
          {loading ? (
            <SkeletonLoader
              count={5}
              styles={{
                height: memoizedGridOptions.rowHeight,
                width: "100%"
              }}
            />
          ) : (
            <div style={{ height: '100%' }}>
              <AgGridTable
                columnDefs={columnDefs}
                data={rowData}
                showSearch={showSearch}
                isRowSelectable={isRowSelectable}
                pagination={false}
                showAppsIcon={false}
                gridOptions={memoizedGridOptions}
                gridRef={gridRef}
                isLoading={loading}
              styles={agGridInlineStyles}
                onRowClick={onRowClick}
                height={"100%"}
                scrollDelay={0}
              />
            </div>
          )}
        </div>
      </div>
      {columDefOn && (
        <ColumnSelectionMenu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          nistColumns={nistColumns}
          cisColumns={cisColumns}
          pciDssColumns={pciDssColumns}
          sparkColumns={sparkColumns}
          onApply={onColumnSelection || (() => {})}
          currentSelectedNist={currentSelectedNist}
          currentSelectedCis={currentSelectedCis}
          currentSelectedPciDss={currentSelectedPciDss}
          currentSelectedSpark={currentSelectedSpark}
        />
      )}
    </div>
  );
};

export default TableCard;
